import { useNavigate } from "react-router-dom";
import { version } from "../version";
import logoImage from "../aditum-logo.jpeg";

export function Welcome() {
  const navigate = useNavigate();

  const handleClientLogin = () => {
    navigate("/login");
  };

  const handleNewClient = () => {
    navigate("/registro");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Elegant Background Pattern */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Large blur circles - top left corner */}
        <circle cx="0" cy="0" r="400" fill="#6366f1" opacity="0.06" filter="blur(40px)" />
        <circle cx="100" cy="100" r="250" fill="#818cf8" opacity="0.05" filter="blur(30px)" />
        
        {/* Large blur circles - bottom right corner */}
        <circle cx="1200" cy="800" r="450" fill="#4f46e5" opacity="0.06" filter="blur(40px)" />
        <circle cx="1100" cy="700" r="300" fill="#818cf8" opacity="0.04" filter="blur(35px)" />
        
        {/* Accent circles - middle areas */}
        <circle cx="1000" cy="200" r="180" fill="#c7d2fe" opacity="0.04" filter="blur(25px)" />
        <circle cx="200" cy="600" r="200" fill="#e0e7ff" opacity="0.05" filter="blur(28px)" />
        
        {/* Subtle wave pattern */}
        <defs>
          <filter id="softGlow">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        
        {/* Curved accent lines for elegance */}
        <path
          d="M0,200 Q300,150 600,200 T1200,200"
          stroke="#6366f1"
          strokeWidth="1.5"
          fill="none"
          opacity="0.08"
          filter="url(#softGlow)"
        />
        <path
          d="M0,600 Q300,550 600,600 T1200,600"
          stroke="#818cf8"
          strokeWidth="1"
          fill="none"
          opacity="0.06"
          filter="url(#softGlow)"
        />
      </svg>

      {/* Welcome Card */}
      <div className="relative z-10 w-full max-w-md mx-auto bg-white rounded-2xl shadow-xl px-6 sm:px-9 py-10 sm:py-12 flex flex-col items-center border border-gray-100">
        {/* Logo */}
        <div className="mb-8 w-full flex items-center justify-center">
          <img
            src={logoImage}
            alt="Aditum Logo"
            className="w-32 sm:w-40 h-auto object-contain"
          />
        </div>

        {/* Form Header */}
        <div className="mb-12 text-center w-full">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Bienvenido</h2>
          <p className="text-base sm:text-lg text-gray-800 font-semibold mb-3">a su portal de autogestión</p>
          <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm font-medium">Selecciona una opcion:</p>
        </div>

        {/* Buttons Container */}
        <div className="w-full flex flex-col gap-4">
          {/* Button 1: Existing Client */}
          <button
            onClick={handleClientLogin}
            className="w-full h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-base font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-blue-200/50"
          >
            <div className="flex items-center justify-center gap-3">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span>Soy un cliente</span>
            </div>
          </button>

          {/* Button 2: New Client */}
          <button
            onClick={handleNewClient}
            className="w-full h-14 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-base font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-indigo-200/50"
          >
            <div className="flex items-center justify-center gap-3">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span>Quiero ser cliente</span>
            </div>
          </button>
        </div>

        {/* Divider */}
        <div className="my-8 flex items-center gap-4 w-full">
          <div className="flex-1 h-px bg-gray-200"></div>
          <span className="text-gray-400 text-xs font-medium">Portal Aditum</span>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Features List */}
        <div className="w-full bg-blue-50 border border-blue-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-700 mb-3 text-center">Beneficios del portal:</p>
          <div className="space-y-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-xs font-medium">Acceso 24/7 a tu cuenta</p>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700 text-xs font-medium">Pagos seguros en línea</p>
            </div>
          </div>
        </div>

        {/* Version Info */}
        <p className="text-center text-gray-400 text-xs mt-8 tracking-wide">versión {version}</p>
      </div>
    </div>
  );
}

export default Welcome;
